import { useEffect, useRef, useState } from "react";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";
import Button from "@/components/buttons/button";
import BaseDialog from "@/models/interfaces/base-dialog";
import ClosableDialog from "@/components/dialogs/closable-dialog";
import { DetectionCategory, DetectionCategoryOption, DetectionCategoryTreatment, PlanDetectionConfigurationDTO, ProjectDetectionConfigurationDTO } from "@/models/interfaces/dtos/plan-detection/plan-detection-options-dto";
import PlanDetectionProvider from "@/models/enums/plan-detection-provider";
import ENDPOINTS from "@/services/endpoints";
import { fetchItem, putItem } from "@/queries/queries-helper";
import DetectionCategoryEditor from "./detection-options/category-editor";
import { categories, categoryLabels, formatCm, validateConfiguration } from "./detection-options/grouping";
import Select from "@/components/select";

interface DialogProps extends BaseDialog {
    projectId: string;
    handleConfirm: (configuration: PlanDetectionConfigurationDTO) => void;
}

const getCategorySummary = (option: DetectionCategoryOption): string => {
    if (!option.enabled) return "Désactivé";
    if (option.treatment === DetectionCategoryTreatment.Range) return `${option.ranges.length} fourchettes`;
    if (Number.isFinite(option.stepCm)) return `Pas de ${formatCm(option.stepCm)} cm`;
    return "Pas à définir";
};

type ProviderOption = {
    value: PlanDetectionProvider;
    label: string;
};

const providerOptions: ProviderOption[] = [
    { value: PlanDetectionProvider.Wisebim, label: "Spécialisé pour les plans anciens" },
    { value: PlanDetectionProvider.Amplify, label: "Spécialisé pour les plans récents" },
];

export default function LaunchPlanDetectionOptions({ projectId, dialogOpen, setDialogOpen, handleConfirm }: DialogProps) {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const [saved, setSaved] = useState<ProjectDetectionConfigurationDTO>();
    const [draft, setDraft] = useState<PlanDetectionConfigurationDTO>();
    const [category, setCategory] = useState(DetectionCategory.Wall);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [reload, setReload] = useState(0);
    const savingRef = useRef(false);
    const generation = useRef(0);

    useEffect(() => {
        generation.current += 1;
        const { current } = generation;
        if (dialogOpen) {
            setLoading(true);
            setSaved(undefined);
            setDraft(undefined);
            setError("");
            setCategory(DetectionCategory.Wall);
            fetchItem<ProjectDetectionConfigurationDTO>(ENDPOINTS.PLAN_DETECTION_CONFIGURATION(projectId))
                .then(value => {
                    if (generation.current !== current) return;
                    if (!value) throw new Error("Configuration du projet indisponible.");
                    setSaved(value);
                    setDraft(structuredClone(value.configuration));
                })
                .catch(e => { if (generation.current === current) setError(e instanceof Error ? e.message : "Impossible de charger les réglages."); })
                .finally(() => { if (generation.current === current) setLoading(false); });
        }
        return () => {
            generation.current += 1;
        };
    }, [projectId, dialogOpen, reload]);

    const errors = draft ? validateConfiguration(draft) : [];
    const confirm = async () => {
        if (!ensureCanEdit()) {
            return;
        }

        if (!draft || !saved || errors.length || savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        setError("");
        const { current } = generation;
        try {
            const response = await putItem<ProjectDetectionConfigurationDTO>(ENDPOINTS.PLAN_DETECTION_CONFIGURATION(projectId), { configuration: draft, rowVersion: saved.rowVersion });
            if (!response) throw new Error("L'enregistrement des réglages n'a pas été confirmé.");
            if (generation.current !== current) return;
            setSaved(response);
            handleConfirm(structuredClone(response.configuration));
            setDialogOpen(false);
        } catch (e) {
            if (generation.current === current) setError(e instanceof Error ? e.message : "Impossible d'enregistrer les réglages.");
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    return <ClosableDialog open={dialogOpen} onClose={() => { if (!savingRef.current) setDialogOpen(false); }} title="Détection des éléments" sizeClass="max-w-3xl max-h-[90vh]" closeOnOutsideClick={!saving} showCloseButton={!saving}>
        <div className="min-h-0 overflow-y-auto px-1">
            <p className="mb-4 text-sm text-gray-500">Définissez comment les éléments détectés deviennent des types de métrés. Ces réglages sont enregistrés pour ce projet au lancement.</p>
            {loading && <p role="status" className="py-6 text-sm">Chargement des réglages du projet…</p>}
            {draft && <>
                <div className="mb-5 flex border-b border-gray-200" aria-label="Catégories à traiter">{categories.map(c => {
                    const option = draft.options[c];
                    return <button type="button" key={c} aria-pressed={category === c} disabled={saving} onClick={() => setCategory(c)} className={`flex flex-1 flex-wrap items-center justify-center gap-2 border-b-2 px-2 py-3 text-sm ${category === c ? "border-indigo-600 bg-indigo-50 text-indigo-600" : "border-transparent"}`}>
                        {categoryLabels[c]}<span className="text-xs text-gray-500">{getCategorySummary(option)}</span>
                    </button>;
                })}</div>
                <DetectionCategoryEditor key={category} category={category} option={draft.options[category]} busy={!canEdit || saving} hasErrors={errors.length > 0} onChange={option => setDraft({ ...draft, options: { ...draft.options, [category]: option } })} />
                {errors.length > 0 && <ul role="alert" className="mt-4 list-inside list-disc text-sm text-red-600">{errors.map(message => <li key={message}>{message}</li>)}</ul>}
            </>}
            {error && <div role="alert" className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700"><p>{error}</p><Button variant="ghost" className="mt-2" disabled={loading || saving} onClick={() => setReload(v => v + 1)}>{draft ? "Abandonner le brouillon et recharger" : "Réessayer"}</Button></div>}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-4">
            {draft && <label className="flex items-center gap-3 text-sm">Lecteur IA
                <Select<ProviderOption>
                    id="plan-detection-provider"
                    className="w-40"
                    isDisabled={!canEdit || saving}
                    options={providerOptions}
                    value={providerOptions.find(p => p.value === draft.provider)}
                    onChange={selected => {
                        if (selected) {
                            setDraft({ ...draft, provider: selected.value });
                        }
                    }}
                />
            </label>}
            <div className="ml-auto flex gap-2"><Button variant="ghost" disabled={saving} onClick={() => setDialogOpen(false)}>Annuler</Button><Button variant="success" size="md" disabled={!canEdit || (loading || saving || !draft || errors.length > 0)} onClick={confirm}>{saving ? "Enregistrement…" : "Lancer la détection"}</Button></div>
        </div>
    </ClosableDialog>;
}
